/**
 * AI CONTEXT
 * Purpose: reusable chat-stream thread of manager-only notes about a staff
 *   member, backed by the flat `staffNotes` collection (deliberately not on
 *   user docs — see StaffNoteConstructor).
 * Reads: staffNotes filtered by subjectUserId; Writes: new notes.
 * Used by: StaffNotesPopover (Standout Review dashboard) and UserProfile's
 *   Staff Notes tab.
 * Refactor: extract the bubble list styling shared with EvalComments.
 */
import { SendOutlined } from "@ant-design/icons"
import { Button, Input, Typography } from "antd"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { sortBy } from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styled from "styled-components"

import { staffNoteAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import Firebase from "#classes/Firebase"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { StaffNoteConstructor } from "#customTypes/composedTypes/standoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import { DatabaseObject } from "#customTypes/types"
import { useFilteredDocs } from "#hooks/jotai/useSubscription"
import { useGetAllUsers } from "#hooks/newHooks/useGetUser"

dayjs.extend(relativeTime)

const { Text } = Typography

const DEFAULT_MAX_HEIGHT = 400

const SNoteList = styled.div<{ $maxHeight: number }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: ${({ $maxHeight }) => $maxHeight}px;
  overflow-y: auto;
  padding: 8px 20px 8px 0;
`

const SNote = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f5f5f5;
`

interface StaffNotesThreadProps {
  subjectUserId: Uuid
  maxHeight?: number
}

const StaffNotesThread = ({
  subjectUserId,
  maxHeight = DEFAULT_MAX_HEIGHT,
}: StaffNotesThreadProps) => {
  const { currentUserId } = useUserContext()
  const [draft, setDraft] = useState("")
  const [isSending, setIsSending] = useState(false)
  const allUsers = useGetAllUsers()

  const usersById = useMemo(
    () => Object.fromEntries(allUsers.map((user) => [user.id, user])),
    [allUsers]
  )

  const queryConfig: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        subjectUserId: {
          value: subjectUserId,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }),
    [subjectUserId]
  )

  const rawNotes = useFilteredDocs<StaffNoteConstructor>(
    staffNoteAtoms,
    DOC_TYPES.STAFF_NOTES,
    queryConfig
  )

  const notes = useMemo(
    () => sortBy(rawNotes, (note) => note.createdAt),
    [rawNotes]
  )

  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = listRef.current
    if (el) {
      el.scrollTop = el.scrollHeight
    }
  }, [notes.length])

  const handleSend = useCallback(async () => {
    if (!currentUserId || !draft.trim()) return

    setIsSending(true)
    try {
      const now = new Date().toISOString()

      const noteData: Omit<StaffNoteConstructor, "id"> = {
        subjectUserId,
        message: draft.trim(),
        createdAt: now,
        createdBy: currentUserId,
        lastUpdatedAt: now,
        lastUpdatedBy: currentUserId,
      }

      await Firebase.getInstance().addDocument<
        StaffNoteConstructor & DatabaseObject
      >(
        DOC_TYPES.STAFF_NOTES,
        noteData as Omit<StaffNoteConstructor & DatabaseObject, "id">
      )

      setDraft("")
    } finally {
      setIsSending(false)
    }
  }, [currentUserId, draft, subjectUserId])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        void handleSend()
      }
    },
    [handleSend]
  )

  return (
    // A plain div, NOT the Flex block: Flex forwards no extra props, so a
    // data-testid on it never renders (see test-environment skill).
    <div
      data-testid="staff-notes-thread"
      style={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      <SNoteList ref={listRef} $maxHeight={maxHeight}>
        {notes.length === 0 && (
          <Text
            type="secondary"
            style={{ textAlign: "center", padding: "24px 0" }}
          >
            No notes yet.
          </Text>
        )}
        {notes.map((note) => {
          const author = usersById[note.createdBy]
          const authorName = author?.name ?? note.createdBy
          const timestamp = dayjs(note.createdAt)

          return (
            <SNote key={note.id} data-testid="staff-note-item">
              <Flex style={{ gap: 6, alignItems: "baseline" }}>
                <Text strong style={{ fontSize: 12 }}>
                  {authorName}
                </Text>
                <Text type="secondary" style={{ fontSize: 10 }}>
                  {timestamp.format("MMM D, YYYY h:mm A")} (
                  {timestamp.fromNow()})
                </Text>
              </Flex>
              <Text style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
                {note.message}
              </Text>
            </SNote>
          )
        })}
      </SNoteList>

      <Flex style={{ gap: 8, marginTop: 8 }}>
        <Input.TextArea
          data-testid="staff-note-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a note... (Enter to send, Shift+Enter for new line)"
          autoSize={{ minRows: 1, maxRows: 4 }}
          style={{ flex: 1 }}
        />
        <Button
          data-testid="staff-note-send"
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          loading={isSending}
          disabled={!draft.trim()}
        />
      </Flex>
    </div>
  )
}

export default StaffNotesThread
